import { ConsolidationIterator } from './consolidation/ConsolidationIterator';
import { NodeConsolidationIterator } from './consolidation/NodeConsolidationIterator';
import { NoOpTraversalController } from './NoOpTraversalController';
import { RenderBatch } from './RenderBatch';
import { USE_OUT_OF_CORE_TILE_MANAGER } from '../globals';


/** @import { ModelIteratorBVH } from "./ModelIteratorBVH" */
/** @import { OutOfCoreTileManager } from "./out-of-core-tile-manager/OutOfCoreTileManager" */
/** @import { Consolidation } from "./consolidation/Consolidation" */
/** @import { FragmentList } from "./FragmentList" */

/**
 * Holds strategies on how to traverse the scene in portions, i.e., batches to render,
 * depending on the data format and its provided hierarchical structures (e.g., BVH, 3D Tiles),
 * as well as on Viewer settings, e.g., if consolidation is enabled.
 */
export class SceneTraversal
{
  // A map holding all available traversal controllers, mapped to their id:
  // { string, ITraversalController }
  #traversalControllerRegistry = new Map();

  // The id of the currently used traversal controller
  #currentTraversalControllerId = "NoOp";

  // Processes render batches, and creates a consolidated version from these, to replace them.
  /** @type {ConsolidationIterator|null} */#consolidationIterator = null;

  // Frustum Intersector
  #frustum = undefined;

  // Integer representing the current draw mode
  #drawMode = undefined;

  // Used to store the original iterators that have been provided in a setInnerAttributes
  // call to check, whether those have changed in the meantime.
  #originalIterators;

  constructor() {
    this.addTraversalController("NoOp", new NoOpTraversalController());
  }

  /**
   * Adds a new traversal controller, to be used depending on available data and other settings.
   * @param {String} id The unique identifier of this traversal controller.
   * @param {ITraversalController} traversalController The instance of the traversal controller.
   */
  addTraversalController(id, traversalController) {
    // If there has been a previous controller for the given id, dispose it
    if (this.#traversalControllerRegistry.has(id)) {
      this.#traversalControllerRegistry.get(id).dtor();
    }
    this.#traversalControllerRegistry.set(id, traversalController);
    // we don't support going back to linear after a bvh is set. Remove the linear iterator so it can be GC'd.
    if (id === "BVH") {
      this.#traversalControllerRegistry.get("Linear")?.dtor();
      this.#traversalControllerRegistry.delete("Linear");
    }
    this.#chooseTraversalController();
  }

  getTraversalController() {
    const traversalController = this.#traversalControllerRegistry.get(this.#currentTraversalControllerId);
    return traversalController;
  }

  /**
   * Updates the current strategy of scene traversal,
   * depending on the data available or custom settings if any.
   */
  #chooseTraversalController() {
    // preferred - traversal based on BVH
    if (this.#traversalControllerRegistry.has("BVH")) {
      this.#currentTraversalControllerId = "BVH";
      return;
    }

    // if available - linear traversal
    if (this.#traversalControllerRegistry.has("Linear")) {
      this.#currentTraversalControllerId = "Linear";
      return;
    }

    // check for a custom traversal
    if (this.#traversalControllerRegistry.has("Custom")) {
      this.#currentTraversalControllerId = "Custom";
      return;
    }

    // if nothing else is applicable, pause traversal, until that changes
    this.#currentTraversalControllerId = "NoOp";
  }

  dtor() {
    // If this model was consolidated, dispose GPU memory of consolidation
    if (this.#consolidationIterator) {
      this.#consolidationIterator.dispose();
      this.#consolidationIterator = null;
    }

    this.#traversalControllerRegistry.forEach((controller) => {
      controller.dtor();
    });

    this.#traversalControllerRegistry = null;
    this.#currentTraversalControllerId = null;
  }

  /**
   * Return the next batch to render
   * @param {number} consolidationDeadline - Time after which to stop consolidation for this frame
   * @returns {RenderBatch|null} The next batch to render
   */
  nextBatch(consolidationDeadline) {
    const renderBatch = this.getTraversalController().nextBatch();

    // no further batches available - we are done with rendering everything for the current view
    if (!renderBatch) {
      return null;
    }

    // return consolidated scene if available for this renderbatch (= individual fragments)
    if (this.#consolidationIterator && renderBatch instanceof RenderBatch) {
      const consolidatedRenderBatch = this.#consolidationIterator.consolidateNextBatch(
        renderBatch,
        this.#frustum,
        this.#drawMode,
        consolidationDeadline);

      // if everything was hidden, we take the render batch we already have
      if (!consolidatedRenderBatch) {
        return renderBatch;
      }

      return consolidatedRenderBatch;
    }

    return renderBatch;
  }

  /**
   * Updates scene traversal by current camera and frustum settings.
   *
   * @param {UnifiedCamera} [camera] The current camera.
   * @param {FrustumIntersector} [frustum] The current frustum.
   * @param {int} [drawMode] The current draw mode index.
   */
  updateView(camera, frustum, drawMode) {
    this.#frustum = frustum;
    this.#drawMode = drawMode;

    this.#chooseTraversalController();

    this.getTraversalController().reset(frustum, camera);

    // notify consolidation iterator that a new traversal has started
    if (this.#consolidationIterator) {
      this.#consolidationIterator.reset();
    }
  }

  resetTransparents() {
    let iterator = this.getTraversalController();
    // TODO: Implement this on all iterators.
    iterator.resetTransparents?.();
  }

  updateBounds() {
    this.getTraversalController().updateBounds();
  }

  getBounds(visibleBounds, hiddenBounds) {
    this.getTraversalController().getBounds(visibleBounds, hiddenBounds);
  }

  /** Returns all render batches considered as 'relevant' to display for the current view.
   *  Used by ground shadows and ground reflection.
   *
   * @returns {RenderBatch[]}
   */
  getScenes() {
    const traversalController = this.getTraversalController();

    // leaflet does not have scenes
    if (traversalController.isModelIteratorTexQuad) {
      return [];
    }

    return traversalController.getGeomScenes();
  }

  getSceneCount() {
    const sceneCount = this.getTraversalController().getSceneCount();
    return sceneCount;
  }

  getInnerAttributes() {
    const attributes = {
      currentIterator: this.getTraversalController(),
      linearIterator: this.#traversalControllerRegistry.get("Linear"),
      bvhIterator: this.#traversalControllerRegistry.get("BVH"),
      consolidationIterator: this.#consolidationIterator
    };
    return attributes;
  }

  setInnerAttributes(attributes) {
    this.#originalIterators = this.#originalIterators || {};

    // add linear iterator and set it to default if it is according to settings
    if (attributes._linearIterator && attributes._linearIterator !== this.#originalIterators._linearIterator) {
      this.#traversalControllerRegistry.set("Linear", attributes._linearIterator.clone());
      this.#originalIterators._linearIterator = attributes._linearIterator;
      if (attributes._linearIterator === attributes._iterator) {
        this.#currentTraversalControllerId = "Linear";
      }
    }

    // add BVH iterator and set it to default if it is according to settings
    if (attributes._bvhIterator && attributes._bvhIterator !== this.#originalIterators._bvhIterator) {
      this.#traversalControllerRegistry.set("BVH", attributes._bvhIterator.clone());
      this.#originalIterators._bvhIterator = attributes._bvhIterator;
      if (attributes._bvhIterator === attributes._iterator) {
        this.#currentTraversalControllerId = "BVH";
      }
    }

    // initialize consolidation iterator if any
    if (attributes._consolidationIterator && attributes._consolidationIterator !== this.#originalIterators._consolidationIterator) {
      this.#originalIterators._consolidationIterator = attributes._consolidationIterator;
      this.#consolidationIterator = attributes._consolidationIterator.clone(this.#traversalControllerRegistry.get("BVH"));
    }

    // Free consolidation iterator if it is no longer used by main viewer (i.e. model got unconsolidated)
    if (this.#consolidationIterator && !attributes._consolidationIterator) {
      this.#consolidationIterator.dispose();
      this.#consolidationIterator = null;
      this.#originalIterators._consolidationIterator = null;
    }

    // If both linearIterator & bvhIterator aren't available, it means that we used a custom iterator.
    // For example, ModelIteratorTexQuad. In this case, try cloning it if is has a clone method.
    // Otherwise, shallow copy is good enough.
    if (!this.#currentTraversalControllerId == "NoOp") {
      this.#traversalControllerRegistry.set("Custom", attributes._iterator?.clone ? attributes._iterator.clone() : attributes._iterator);
      this.#currentTraversalControllerId = "Custom";
    }
  }

  // Adds a fragment to the list of the linear model iterator.
  // NOTE that other iterators do not support this function.
  // @param int [fragId] The Fragment's ID to be added.
  addFragment(fragId) {
    // The linear iterator can be updated to add meshes incrementally.
    // The BVH iterator is not mutable, yet.
    if (this.#currentTraversalControllerId == "Linear") {
      this.getTraversalController().addFragment(fragId);
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Leaflet specific functionality

  // Sets the MESH_VISIBLE flag for a fragment (true=visible, false=ghosted).
  // NOTE that is only supported by the Leaflet iterator.
  setVisibilityLeaflet(value) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      this.getTraversalController().setVisibility(value);
    }
  }

  /**
   * Returns true if the Leaflet Model needs a redraw.
   *
   * @params  {number} timeStamp
   * @returns {bool}   true if the model is a leaflet and it needs a redraw
   */
  needsRedrawLeaflet(timeStamp) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      return this.getTraversalController().update(timeStamp);
    }

    // assume constant scene otherwise
    return false;
  }

  clearThemingColorsLeaflet() {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      this.getTraversalController().clearThemingColor();
    }
  }

  setThemingColorLeaflet(color) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      // dbId is ignored in this case, as well as intensity. Apply theming to whole model
      this.getTraversalController().setThemingColor(color);
      return true;
    }
    return false;
  }

  getLeafletIterator() {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      return this.getTraversalController();
    }
    return null;
  }

  setModelTransformLeaflet(matrix) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      this.getTraversalController().setModelMatrix(matrix);
    }
  }

  getModelTransformLeaflet() {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      return this.getTraversalController().getModelMatrix();
    }
    return undefined;
  }

  getInverseModelTransformLeaflet() {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      return this.getTraversalController().getInverseModelMatrix();
    }

    return undefined;
  }

  setViewportBoundsLeaflet(bounds) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      this.getTraversalController().setViewBounds(bounds);
    }
    return undefined;
  }

  getViewportBoundsLeaflet() {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      return this.getTraversalController().getViewBounds();
    }
    return undefined;
  }

  setDoNotCutLeaflet(doNotCut) {
    if (this.getTraversalController().isModelIteratorTexQuad) {
      this.getTraversalController().setDoNotCut(doNotCut);
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Consolidation specific functionality

  updateModelTransformConsolidation() {
    this.#consolidationIterator && this.#consolidationIterator.modelMatrixChanged();
  }

  /**
   * Enables and initializes consolidation.
   *
   * @param {FragmentList} fragmentList The current FragmentList.
   * @param {Consolidation} consolidation The results of consolidation.
   * @param {ModelIteratorBVH} bvhModelIterator The BVH model iterator.
   * @param {MaterialManager} [materialManager] The current MaterialManager. Required with per tile consolidation.
   */
  enableConsolidation(fragmentList, consolidation, bvhModelIterator, materialManager) {
    if (this.#consolidationIterator)
    return;

    // make BVHIterator use the consolidation when possible
    if (USE_OUT_OF_CORE_TILE_MANAGER)
    this.#consolidationIterator = new NodeConsolidationIterator(fragmentList, bvhModelIterator, materialManager);else

    this.#consolidationIterator = new ConsolidationIterator(fragmentList, consolidation, bvhModelIterator);
  }

  disableConsolidation() {
    this.#consolidationIterator.dispose();
    this.#consolidationIterator = null;
  }

  /**
   * Returns the consolidation for this model
   * @param {number} [nodeId] - The id of the bvh node to get the consolidation for. If not specified, the consolidation for the entire model is returned.
   *                               (if incremental consolidation is disabled) or an array with consolidations for each tile is returned (if incremental consolidation is enabled).
   * @returns {Consolidation|Consolidation[]|undefined} The consolidation for this model/node, an array with per tile consolidations or undefined if there is no consolidation
   */
  getConsolidation(nodeId) {
    const consolidation = this.#consolidationIterator?.getConsolidation(nodeId);
    return consolidation;
  }

  /**
   * Sets the consolidation for a specific bvh node
   * @param {number} nodeId
   * @param {Consolidation|undefined} consolidation
   */
  setConsolidation(nodeId, consolidation) {
    this.#consolidationIterator?.setConsolidation(nodeId, consolidation);
  }

  isEnabledConsolidation() {
    return !!this.#consolidationIterator;
  }

  /**
   * This function is only needed if...
   *
   *   1. You want to draw a fragment to an overlay scene that overdraws the original fragment, and
   *   2. Consolidation is used for this model.
   *
   *  To avoid flickering artifacts, the geometry used for the overlay scene must exactly match with the
   *  one used for the main scene rendering. However, when consolidation is used, this geometry may vary
   *  and (slightly) differ from the original fragment geometry.
   *
   *  This function updates the given render proxy to make it exactly match with the geometry used for the
   *  the last main scene rendering. This involves to replace geometry, material, and matrix when necessary.
   *
   *  NOTE: An updated proxy can exclusively be used for rendering. Do not use this function if you want to
   *        access any vertex data directly.
   *
   *   @param {THREE.Mesh} proxy  - currently used proxy mesh to represent the fragment
   *   @param {Number}     fragId - fragment represented by this proxy */
  updateRenderProxy(proxy, fragId) {
    if (!this.isEnabledConsolidation()) {
      // nothing to do - rendering will always use the original geometry anyway.
      return;
    }

    // fragment might be consolidated
    this.#consolidationIterator.updateRenderProxy(proxy, fragId);
  }
}